import { useEffect } from 'react';
import './NightDazeAnimation.css';

/**
 * PROFESSIONAL NIGHT DAZE ANIMATION
 * Dark-type special move with shadow burst effects
 * 
 * Key Features:
 * - High-power dark energy burst (Night Daze signature move)
 * - Shadow manipulation effects with purple/black energy
 * - 40% chance accuracy reduction visual effect
 * - Professional gaming company quality with dramatic timing
 * - Dark-type themed effects with shadow burst
 */
export default function NightDazeAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = (playerRect.left + playerRect.width / 2) - containerRect.left;
    const playerY = (playerRect.top + playerRect.height / 2) - containerRect.top;
    const opponentX = (opponentRect.left + opponentRect.width / 2) - containerRect.left;
    const opponentY = (opponentRect.top + opponentRect.height / 2) - containerRect.top;

    // Calculate trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Dark Energy Buildup (0-400ms)
    console.log('Night Daze Phase 1: Dark Energy Buildup');
    
    // Player enhancement with dark aura
    playerEl.classList.add('night-daze-user-charge');
    
    // Dark shadow aura around player
    const shadowAura = document.createElement('div');
    shadowAura.className = 'night-daze-shadow-aura';
    shadowAura.style.left = `${playerX - 80}px`;
    shadowAura.style.top = `${playerY - 80}px`;
    container.appendChild(shadowAura);
    elements.push(shadowAura);

    // Dark energy particles swirling around player
    for (let i = 0; i < 20; i++) {
      const particle = document.createElement('div');
      particle.className = 'night-daze-dark-particle';
      const angle = (i / 20) * 360;
      const radius = 60 + (i % 3) * 20;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      particle.style.left = `${particleX - 6}px`;
      particle.style.top = `${particleY - 6}px`;
      particle.style.animationDelay = `${i * 20}ms`;
      particle.style.setProperty('--orbit-radius', `${radius}px`);
      container.appendChild(particle);
      elements.push(particle);
    }

    // Screen darkening effect
    const screenDarken = document.createElement('div');
    screenDarken.className = 'night-daze-screen-darken';
    container.appendChild(screenDarken);
    elements.push(screenDarken);

    // PHASE 2: Dark Energy Projectile (400-800ms)
    setTimeout(() => {
      console.log('Night Daze Phase 2: Dark Energy Projectile');
      
      // Create dark energy orb
      const darkOrb = document.createElement('div');
      darkOrb.className = 'night-daze-dark-orb';
      darkOrb.style.left = `${playerX - 25}px`;
      darkOrb.style.top = `${playerY - 25}px`;
      darkOrb.style.setProperty('--target-x', `${dx}px`);
      darkOrb.style.setProperty('--target-y', `${dy}px`);
      darkOrb.style.setProperty('--rotation', `${angle}deg`);
      container.appendChild(darkOrb);
      elements.push(darkOrb);

      // Dark energy trail following orb
      const darkTrail = document.createElement('div');
      darkTrail.className = 'night-daze-dark-trail';
      darkTrail.style.left = `${playerX - 5}px`;
      darkTrail.style.top = `${playerY - 5}px`;
      darkTrail.style.width = `${distance}px`;
      darkTrail.style.transform = `rotate(${angle}deg)`;
      darkTrail.style.transformOrigin = 'left center';
      container.appendChild(darkTrail);
      elements.push(darkTrail);

      // Shadow tendrils extending from player
      for (let i = 0; i < 8; i++) {
        const tendril = document.createElement('div');
        tendril.className = 'night-daze-shadow-tendril';
        const tendrilAngle = (i / 8) * 360;
        const tendrilLength = 80 + Math.random() * 40;
        tendril.style.left = `${playerX}px`;
        tendril.style.top = `${playerY}px`;
        tendril.style.width = `${tendrilLength}px`;
        tendril.style.transform = `rotate(${tendrilAngle}deg)`;
        tendril.style.transformOrigin = 'left center';
        tendril.style.animationDelay = `${i * 50}ms`;
        container.appendChild(tendril);
        elements.push(tendril);
      }
    }, 400);

    // PHASE 3: Dark Explosion Impact (800-1400ms)
    setTimeout(() => {
      console.log('Night Daze Phase 3: Dark Explosion Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('night-daze-opponent-hit');

      // Massive dark explosion at opponent
      const darkExplosion = document.createElement('div');
      darkExplosion.className = 'night-daze-dark-explosion';
      darkExplosion.style.left = `${opponentX - 100}px`;
      darkExplosion.style.top = `${opponentY - 100}px`;
      container.appendChild(darkExplosion);
      elements.push(darkExplosion);

      // Multiple shadow waves expanding outward
      for (let i = 0; i < 4; i++) {
        setTimeout(() => {
          const shadowWave = document.createElement('div');
          shadowWave.className = 'night-daze-shadow-wave';
          shadowWave.style.left = `${opponentX - 60}px`;
          shadowWave.style.top = `${opponentY - 60}px`;
          shadowWave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shadowWave);
          elements.push(shadowWave);
        }, i * 120);
      }

      // Dark energy fragments scattering
      for (let i = 0; i < 16; i++) {
        const fragment = document.createElement('div');
        fragment.className = 'night-daze-dark-fragment';
        const fragmentAngle = (i / 16) * 360;
        const fragmentRadius = 80 + Math.random() * 60;
        const fragmentX = opponentX + Math.cos(fragmentAngle * Math.PI / 180) * fragmentRadius;
        const fragmentY = opponentY + Math.sin(fragmentAngle * Math.PI / 180) * fragmentRadius;
        fragment.style.left = `${fragmentX - 4}px`;
        fragment.style.top = `${fragmentY - 4}px`;
        fragment.style.animationDelay = `${i * 30}ms`;
        fragment.style.setProperty('--scatter-x', `${Math.cos(fragmentAngle * Math.PI / 180) * 40}px`);
        fragment.style.setProperty('--scatter-y', `${Math.sin(fragmentAngle * Math.PI / 180) * 40}px`);
        container.appendChild(fragment);
        elements.push(fragment);
      }

      // Shadow vortex at impact point
      const shadowVortex = document.createElement('div');
      shadowVortex.className = 'night-daze-shadow-vortex';
      shadowVortex.style.left = `${opponentX - 75}px`;
      shadowVortex.style.top = `${opponentY - 75}px`;
      container.appendChild(shadowVortex);
      elements.push(shadowVortex);
    }, 800);

    // PHASE 4: Accuracy Reduction Effect (1400-1800ms)
    setTimeout(() => {
      console.log('Night Daze Phase 4: Accuracy Reduction Effect');
      
      // Remove charge effect
      playerEl.classList.remove('night-daze-user-charge');
      
      // Accuracy reduction indicator removed - visual effects only

      // Blinding shadow effect around opponent
      const blindingEffect = document.createElement('div');
      blindingEffect.className = 'night-daze-blinding-effect';
      blindingEffect.style.left = `${opponentX - 60}px`;
      blindingEffect.style.top = `${opponentY - 60}px`;
      container.appendChild(blindingEffect);
      elements.push(blindingEffect);

      // Swirling darkness particles around opponent
      for (let i = 0; i < 12; i++) {
        const darknessParticle = document.createElement('div');
        darknessParticle.className = 'night-daze-darkness-particle';
        const angle = (i / 12) * 360;
        const radius = 50;
        const particleX = opponentX + Math.cos(angle * Math.PI / 180) * radius;
        const particleY = opponentY + Math.sin(angle * Math.PI / 180) * radius;
        darknessParticle.style.left = `${particleX - 4}px`;
        darknessParticle.style.top = `${particleY - 4}px`;
        darknessParticle.style.animationDelay = `${i * 35}ms`;
        container.appendChild(darknessParticle);
        elements.push(darknessParticle);
      }
    }, 1400);

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Night Daze: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('night-daze-user-charge');
      opponentEl.classList.remove('night-daze-opponent-hit');
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1800);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('night-daze-user-charge');
      opponentEl.classList.remove('night-daze-opponent-hit');
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
NightDazeAnimation.moveType = 'dark';
